
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp, User, MapPin, GraduationCap, Star } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import type { TeacherMatchProfile } from '@/hooks/useTeacherMatch';

interface Props {
  teacher: TeacherMatchProfile;
  matchedAttributes: string[];
  selected?: boolean;
  onToggleSelect?: () => void;
}

export const TeacherMatchCard: React.FC<Props> = ({ teacher, matchedAttributes, selected, onToggleSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const matchSet = new Set(matchedAttributes);

  const subjectBadge = (text: string, isMatched: boolean) => (
    <Badge
      key={text}
      variant={isMatched ? 'default' : 'outline'}
      className={isMatched ? 'bg-primary/90 text-primary-foreground font-semibold' : ''}
    >
      {text}
    </Badge>
  );

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
         <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {onToggleSelect && (
              <Checkbox
                checked={selected}
                onCheckedChange={onToggleSelect}
                className="mt-1"
              />
            )}
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">{teacher.name}</span>
                {teacher.nickname && (
                  <span className="text-sm text-muted-foreground">({teacher.nickname})</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="font-mono">{teacher.code}</span>
                {teacher.tier && <Badge variant="outline" className="text-xs">{teacher.tier}</Badge>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {teacher.gender && (
              <Badge variant="secondary" className="text-xs">{teacher.gender}</Badge>
            )}
            {teacher.personality && (
              <Badge variant="secondary" className="text-xs">{teacher.personality}</Badge>
            )}
            <Badge variant={teacher.status === 'ACTIVE' ? 'default' : 'destructive'} className="text-xs">
              {teacher.status}
            </Badge>
          </div>
        </div>

        {/* Quick info */}
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          {teacher.university && (
            <span className="flex items-center gap-1">
              <GraduationCap className="h-3.5 w-3.5" />
              {teacher.university} {teacher.major && `- ${teacher.major}`}
            </span>
          )}
          {teacher.branches.length > 0 && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {teacher.branches.map(b => (
                <Badge key={b} variant={matchSet.has(b) ? 'default' : 'outline'} className="text-xs">
                  {b}
                </Badge>
              ))}
            </span>
          )}
          {teacher.trial_lesson && (
            <span className="flex items-center gap-1">
              <Star className="h-3.5 w-3.5" />
              ทดลองสอน: {teacher.trial_lesson}
            </span>
          )}
        </div>

        {/* Subjects */}
        {teacher.subjects.length > 0 && (
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">วิชาที่สอน</span>
            <div className="flex flex-wrap gap-1.5">
              {teacher.subjects.map((s, i) => {
                const label = `${s.subject} (${s.education_stage}${s.course_type ? ` - ${s.course_type}` : ''})`;
                const isMatched = matchSet.has(s.subject) || matchSet.has(s.education_stage);
                return subjectBadge(label, isMatched);
              })}
            </div>
          </div>
        )}

        {/* Specialized & Teaching Styles */}
        <div className="flex flex-wrap gap-4">
          {teacher.specialized.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">เฉพาะทาง</span>
              <div className="flex flex-wrap gap-1">
                {teacher.specialized.map(s => subjectBadge(s, matchSet.has(s)))}
              </div>
            </div>
          )}
          {teacher.teaching_styles.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">สไตล์การสอน</span>
              <div className="flex flex-wrap gap-1">
                {teacher.teaching_styles.map(s => (
                  <Badge key={s.teaching_style} variant={matchSet.has(s.teaching_style) ? 'default' : 'outline'} className="text-xs">
                    {s.teaching_style} (Lv.{s.level})
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {teacher.student_proficiency.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">ระดับนักเรียนที่เหมาะ</span>
              <div className="flex flex-wrap gap-1">
                {teacher.student_proficiency.map(p => subjectBadge(p, matchSet.has(p)))}
              </div>
            </div>
          )}
        </div>

        {/* Expandable: Certificates */}
        {teacher.certificates.length > 0 && (
          <Collapsible open={expanded} onOpenChange={setExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-xs w-full justify-between">
                ใบรับรอง ({teacher.certificates.length})
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">Grade</th>
                      <th className="px-3 py-1.5 text-left font-medium">Status</th>
                      <th className="px-3 py-1.5 text-left font-medium">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teacher.certificates.map((c, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-1.5">{c.grade || '-'}</td>
                        <td className="px-3 py-1.5">{c.status || '-'}</td>
                        <td className="px-3 py-1.5">{c.note || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
};
